import React from 'react';
import { useToast } from '../contexts/ToastContext';
import './ErrorBoundary.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  // Utility function to safely extract error message
  static getErrorMessage(error) {
    if (!error) return 'Unknown error';
    if (typeof error === 'string') return error;
    if (error instanceof Error) return error.message || error.toString();
    if (error.message) return error.message;
    // Handle Event objects specifically
    if (error instanceof Event) {
      // Try to extract error from event.error if available
      if (error.error) {
        return ErrorBoundary.getErrorMessage(error.error);
      }
      // Try to get message from event
      if (error.message) return error.message;
      return 'An unexpected event occurred';
    }
    // Try toString as last resort
    const errorString = String(error);
    // If it's [object Event] or similar, return a more helpful message
    if (errorString === '[object Event]' || errorString === '[object Object]') {
      return 'An unexpected error occurred';
    }
    return errorString;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    try {
      const errorMessage = ErrorBoundary.getErrorMessage(error);
      console.error('ErrorBoundary caught an error:', errorMessage, error, errorInfo);
      this.setState({ errorInfo });
      
      // Log to analytics if available
      if (window.analytics && typeof window.analytics.track === 'function') {
        try {
          window.analytics.track('Error Boundary Caught Error', {
            error: errorMessage,
            stack: error instanceof Error ? error.stack : '',
            componentStack: errorInfo?.componentStack || '',
            timestamp: new Date().toISOString()
          });
        } catch (analyticsError) {
          // Silently fail analytics tracking
          console.warn('Failed to track error in analytics:', analyticsError);
        }
      }
    } catch (boundaryError) {
      // Even error handling can fail, so we need to be extra careful
      console.error('Error in ErrorBoundary error handler:', boundaryError);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback 
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          fallback={this.props.fallback}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ error, errorInfo, onRetry, fallback }) => {
  const { showError } = useToast();
  
  React.useEffect(() => {
    try {
      if (showError && typeof showError === 'function') {
        showError('Something went wrong. Please try again.', {
          actions: [
            { 
              label: 'Retry', 
              primary: true, 
              onClick: () => {
                try {
                  onRetry();
                } catch (retryError) {
                  console.error('Error in retry handler:', retryError);
                }
              }
            }
          ],
          duration: 0 // Don't auto-dismiss
        });
      }
    } catch (toastError) {
      // Silently fail if toast system is broken
      console.error('Failed to show error toast:', toastError);
    }
  }, [showError, onRetry]);

  if (fallback) {
    return fallback;
  }

  return (
    <div className="error-boundary-fallback">
      <div className="error-boundary-content">
        <div className="error-boundary-icon">⚠️</div>
        <h2 className="error-boundary-title">Something went wrong</h2>
        <p className="error-boundary-message">
          We're sorry, but something unexpected happened. Our team has been notified.
        </p>
        <div className="error-boundary-actions">
          <button 
            className="error-boundary-retry-button"
            onClick={onRetry}
          >
            Try Again
          </button>
          <button 
            className="error-boundary-reload-button"
            onClick={() => window.location.reload()}
          >
            Reload Page
          </button>
        </div>
        {process.env.NODE_ENV === 'development' && (
          <details className="error-boundary-details">
            <summary>Error Details (Development)</summary>
            <pre className="error-boundary-stack">
              {ErrorBoundary.getErrorMessage(error)}
              {error instanceof Error && error.stack && `\n\nStack:\n${error.stack}`}
              {errorInfo && errorInfo.componentStack && `\n\nComponent Stack:\n${errorInfo.componentStack}`}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

export default ErrorBoundary;